import React, { useCallback, useContext, useState } from 'react';

import DownloadIcon from '@mui/icons-material/Download';
import { CircularProgress, Tooltip } from '@mui/material';
import Button from '@mui/material/Button';
import DmsCoordinates from "dms-conversion";
import { nanoid } from 'nanoid';
import numeral from 'numeral';
import { GetDataDownloadLinkFormat, GetDataDownloadLinkProps, getDataDownloadLink } from '../api/data';
import { AccountContext } from '../context/account';
import { ProjectItem, UserCustomDataPoint } from '../core/global';
import { isNumber } from '../helpers/core';

const saveFile = (url: string, filename: string) => {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "file-name";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

const startDownload = async (url: string, filename: string, callback: () => void) => {
  try {
    const response = await fetch(url, {
      headers: {
        Accept:
          "application/json,text/plain,application/zip,image/png,image/jpeg,image/*,application/x-matlab-data,binary/octet-stream",
      },
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    saveFile(blobUrl, filename);
    URL.revokeObjectURL(blobUrl);
    callback()
  } catch (err) {
    console.error("Error in fetching and downloading file:", err);
    callback()
  }
}

const getFilename = (item: ProjectItem | UserCustomDataPoint, suffix: string, lat: number, lon: number) => {
  const {
    name,
    id,
    model,
    provider,
    geography,
    version,
    path,
  } = item

  if (path) {
    const useModel = (
      provider === null 
      || model === null 
      || geography === null
      ) ? `custom data` : `${provider}_${model}_${geography.substring(0, 3)}${version ? `_v${version}` : ``}`.toUpperCase()

    const [, , filename] = path.split('/')
    let start = filename.slice(0,7)
    let end = filename.slice(7,14)
    if (isNumber(lat) && isNumber(lon)) {
      const dmsCoords = new DmsCoordinates(Number(lat), Number(lon));
      const { longitude, latitude } = dmsCoords.dmsArrays;
      const [lat_d, lat_m, lat_s , lat_cardinal] = latitude;
      const [lon_d, lon_m, lon_s, lon_cardinal] = longitude;
      start = `${numeral(lat).format("0.000").replace(/[.]/gi, 'p')}${lat_cardinal}`
      end = `${numeral(lon).format("0.000").replace(/[.]/gi, 'p')}${lon_cardinal}`
    }
    return `${useModel}_${start}_${end}.${suffix}`
  }
  return `${id}-${name}.${suffix}`
}

interface ProjectItemProps {
  item: ProjectItem | UserCustomDataPoint
  isCustomData?: boolean
}

export const ProjectItemDownloadButtons = ({
  item,
  isCustomData = false,
}: ProjectItemProps) => {
  const [loadingCSV, setLoadingCSV] = useState<number[]>([])
  const [loadingMAT, setLoadingMAT] = useState<number[]>([])
  const [loadingCSVError, setLoadingCSVError] = useState<boolean>(false)
  const [loadingMATError, setLoadingMATError] = useState<boolean>(false)
  
  const {
    refetchProjects,
    setSelectedPoint,
    setUserMessages
  } = useContext(AccountContext);

  const {
    lat,
    lon,
    id,
    download,
    path,
    model = null,
    provider = null,
    geography = null,
    version = null,
  } = item

  const createMessage = useCallback((label: string, format: string, link: string = '', error: boolean = false) => {
    setUserMessages(m => {
      if (error) {
        return [
          ...m,
          {
            id: nanoid(),
            subject: 'Download link cannot be generated',
            message: `Your download for **${label}** in ${format} format cannot be generated. Please try again later or reach out to our team for support.`,
            status: "new",
            severity: "error",
          }
        ]
      }
      return [
        ...m,
        {
          id: nanoid(),
          subject: 'Download link has been generated',
          message: `Your download for **${label}** in ${format} format is ready. If your download has not started automatically, <a href="${link}" download>please click here to download</a>.`,
          status: "new",
          severity: "success",
        }
      ]
    })
  }, [])

  const getDownloadLink = useCallback(async (item: UserCustomDataPoint | ProjectItem, format: GetDataDownloadLinkFormat) => {
    const {id, path, download} = item

    // Check if the download link is already present
    if (download && typeof download === 'object') {
      try {
        if (Object.prototype.hasOwnProperty.call(download, format)) {
          const filePath = download[format]
          if (filePath && typeof filePath === 'string' && filePath.length) {
            return filePath
          }
        }
      } catch (e) {
      }
    }

    const executeDownload = async () => {
      const params: GetDataDownloadLinkProps = {
        orderId: null,
        filename: null,
        id,
        format,
        model,
        provider,
        geography,
        version,
      }
      if (path && typeof path === 'string') {
        const [, transaction_id, filename] = path.split('/')
        params.orderId = transaction_id,
        params.filename = filename.split('.')[0]
      }
      if (params.orderId) {
        const result = await getDataDownloadLink(params, isCustomData)
        const {response} = result        
        return response
      }
      return null
    }

    const link = await executeDownload()
    return link
  }, [isCustomData] )

  return (
    <>
      <Tooltip
        title={loadingCSVError? 'Error generating CSV' : 'Download CSV [UTF-8]'} 
        placement="bottom" 
        arrow
      >
        <Button
          style={loadingCSVError ? {color: '#ff0000', marginLeft: 0} : {marginLeft: 0}}
          // loading={loadingCSV.includes(id)}
          // loadingPosition='start'
          variant="text"
          size="small"
          startIcon={loadingCSV.includes(id)? null : <DownloadIcon />}
          onClick={(e: React.SyntheticEvent) => {
            e.preventDefault()
            setLoadingCSVError(false)
            const executeDownload = async () => {
              setLoadingCSV((list) => [...list, id])
              const link = await getDownloadLink(item, 'csv')
              const useLabel = item?.name || `${lat} ${lon}`
              if (link && typeof link === 'string') { 
                startDownload(link, getFilename(item, 'csv', lat, lon), () => {
                  setLoadingCSV(list => [...list].filter( i => i !== id))
                  refetchProjects()
                  createMessage(useLabel, 'CSV', link)
                })
              } else {
                setLoadingCSVError(true)
                setLoadingCSV(list => [...list].filter( i => i !== id))
                createMessage(useLabel, 'CSV', '', true)
              }
            }
            executeDownload()
          }}
        >
          {loadingCSV.includes(id) && <span style={{paddingRight: '6px', display: 'flex', alignContent: 'center'}}>
            <CircularProgress size={16} />
          </span>}
          CSV
        </Button>
      </Tooltip>
      <Tooltip
        title={loadingMATError? 'Error generating MAT ' : 'Download MAT'} 
        placement="bottom" 
        arrow
      >
        <Button
          style={loadingMATError ? {color: '#ff0000'} : {}}
          // loading={loadingMAT.includes(id)}
          // loadingPosition='start'
          variant="text"
          size="small"
          startIcon={loadingMAT.includes(id)? null : <DownloadIcon />}
          onClick={(e: React.SyntheticEvent) => {
            e.preventDefault()
            setLoadingMATError(false)
            const executeDownload = async () => {
              setLoadingMAT((list) => [...list, id])
              const link = await getDownloadLink(item, 'mat')
              const useLabel = item?.name || `${lat} ${lon}`
              if (link && typeof link === 'string') { 
                startDownload(link, getFilename(item, 'mat', lat, lon), () => {
                  setLoadingMAT(list => [...list].filter( i => i !== id))
                  refetchProjects()
                  createMessage(useLabel, 'MAT', link)  
                })
              } else {
                setLoadingMATError(true)
                setLoadingMAT(list => [...list].filter( i => i !== id))
                createMessage(useLabel, 'MAT', '', true)  
              }
            }
            executeDownload()
          }}
        >
          {loadingMAT.includes(id) && <span style={{paddingRight: '6px', display: 'flex', alignContent: 'center'}}>
            <CircularProgress size={16} />
          </span>}
          MAT
        </Button>
      </Tooltip>
    </>
  )
}